import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useCart } from "../context/CartContext";
import { FaCheckCircle, FaArrowLeft, FaStar } from "react-icons/fa";
import TravelerDetailsModal from "../components/TravelerDetailsModal";
import usePageMeta from "../hooks/usePageMeta";
import { pageTitle, hotelSchema, breadcrumbSchema } from "../lib/seo";
import "../styles/DetailPage.css";

function HotelDetailNew() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchHotel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  async function fetchHotel() {
    try {
      const { data } = await supabase
        .from("hotels")
        .select("*")
        .eq("id", hotelId)
        .single();
      setHotel(data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }

  function formatPrice(price) {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  }

  function handleBookNow() {
    setShowModal(true);
  }

  function handleModalSubmit(travelerDetails) {
    if (!hotel) return;
    
    const cartItem = {
      id: hotel.id,
      name: hotel.name,
      type: 'hotel',
      price: formatPrice(hotel.price_per_night || 0),
      image: hotel.image,
      destination: `${hotel.city}, ${hotel.country}`,
      city: hotel.city,
      country: hotel.country,
    };
    
    addToCart(cartItem, travelerDetails);
    setShowModal(false);
    navigate("/cart");
  }

  usePageMeta({
    title: hotel ? pageTitle(`${hotel.name} — ${hotel.city}, ${hotel.country}`) : undefined,
    description: hotel ? `${hotel.stars ? `${hotel.stars}-star hotel` : "Hotel"} in ${hotel.city}, ${hotel.country} from ${formatPrice(hotel.price_per_night || 0)} per night. Check amenities and book online.` : undefined,
    image: hotel?.image,
    jsonLd: hotel ? [
      hotelSchema(hotel),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Hotels", path: "/hotels" },
        { name: hotel.name, path: `/hotels/${hotel.id}` },
      ]),
    ] : undefined,
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!hotel) {
    return (
      <div className="empty-state" style={{ minHeight: "60vh" }}>
        <div className="empty-state-icon">🏨</div>
        <h3>Hotel not found</h3>
        <Link to="/hotels/">
          <button className="card-cta">Browse Hotels</button>
        </Link>
      </div>
    );
  }

  return (
    <div className="detail-page">
      <div className="detail-hero">
        <img
          src={hotel.image || "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200"}
          alt={hotel.name}
          className="detail-hero-image"
        />
        <div className="detail-hero-overlay">
          <div className="detail-hero-content">
            <h1 className="detail-title">{hotel.name}</h1>
            <p className="detail-subtitle">
              <FaStar style={{ marginRight: 8 }} />
              {hotel.stars || hotel.rating || 4.5} Star Hotel in {hotel.city}
            </p>
          </div>
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-main">
          <Link to="/hotels/" className="back-link">
            <FaArrowLeft /> Back to Hotels
          </Link>

          {hotel.description && (
            <div className="detail-section">
              <h2 className="section-title">About This Hotel</h2>
              <p style={{ color: "var(--text-light)", lineHeight: 1.7 }}>
                {hotel.description}
              </p>
            </div>
          )}

          {hotel.amenities && hotel.amenities.length > 0 && (
            <div className="detail-section">
              <h2 className="section-title">Amenities</h2>
              <ul className="detail-list">
                {hotel.amenities.map((item, index) => (
                  <li key={index}>
                    <FaCheckCircle />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hotel.services && hotel.services.length > 0 && (
            <div className="detail-section">
              <h2 className="section-title">Services</h2>
              <ul className="detail-list">
                {hotel.services.map((item, index) => (
                  <li key={index}>
                    <FaCheckCircle />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="detail-sidebar">
          <div className="booking-card">
            <div className="booking-price">{formatPrice(hotel.price_per_night || 0)}</div>
            <div className="booking-label">per night</div>
            <button className="booking-cta" onClick={handleBookNow}>
              Book Now
            </button>
            <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid var(--border)" }}>
              <div style={{ fontSize: 14, color: "var(--text-light)", marginBottom: 12 }}>
                <strong>Location:</strong> {hotel.city}, {hotel.country || "India"}
              </div>
              <div style={{ fontSize: 14, color: "var(--text-light)" }}>
                <strong>Rating:</strong> {hotel.stars || hotel.rating || 4.5} / 5.0
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Traveler Details Modal */}
      <TravelerDetailsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleModalSubmit}
        itemName={hotel?.name}
      />
    </div>
  );
}

export default HotelDetailNew;